import { ref } from 'vue'
import type { Chat as Conversation } from 'models'
import { getTextPart, stripMarkdown } from 'utils'
import { useDialogConfirm } from '../useDialogConfirm'

/**
 * Single-line preview for a conversation: explicit title, otherwise the
 * first user message. Used as list label and as search haystack.
 */
export function getConversationPreview(conversation: Conversation): string {
    return (
        stripMarkdown(conversation.title) ||
        stripMarkdown(
            getTextPart(
                // The list endpoint isn't Zod-parsed: don't trust the
                // declared types at runtime
                conversation.messages?.find(
                    (message) => message.role === 'user',
                ),
            ),
        ) ||
        ''
    )
}

const DAY_MS = 86_400_000

/**
 * Date sections of the conversation lists (PkChatSidebar,
 * PkChatSearchDialog), newest first: today, yesterday, the last 7 and 30
 * days, older. Keeps the order of the input and drops the empty sections.
 */
export function groupConversationsByDate(conversations: Conversation[]) {
    const startOfToday = new Date().setHours(0, 0, 0, 0)
    const buckets = {
        today: [] as Conversation[],
        yesterday: [] as Conversation[],
        last7Days: [] as Conversation[],
        last30Days: [] as Conversation[],
        older: [] as Conversation[],
    }
    for (const conversation of conversations) {
        // lastMessageAt is typed as Date but arrives as an ISO string at
        // runtime (the chatbot API client doesn't parse through Zod)
        const time = conversation.lastMessageAt
            ? new Date(conversation.lastMessageAt).getTime()
            : 0
        if (time >= startOfToday) {
            buckets.today.push(conversation)
        } else if (time >= startOfToday - DAY_MS) {
            buckets.yesterday.push(conversation)
        } else if (time >= startOfToday - 7 * DAY_MS) {
            buckets.last7Days.push(conversation)
        } else if (time >= startOfToday - 30 * DAY_MS) {
            buckets.last30Days.push(conversation)
        } else {
            buckets.older.push(conversation)
        }
    }
    return (Object.entries(buckets) as [keyof typeof buckets, Conversation[]][])
        .filter(([, items]) => items.length > 0)
        .map(([key, items]) => ({ key, items }))
}

/**
 * Inline-rename and delete-with-confirm state shared by the conversation
 * lists (PkChatbotViewConversations, PkChatSidebar). Renames are optimistic:
 * on failure the next conversations load reconciles with the server state.
 */
export function useConversationActions(actions: {
    renameChatTitle: (chatId: string, title: string) => Promise<unknown>
    deleteChat: (chatId: string) => Promise<unknown>
}) {
    const editingId = ref<string | null>(null)
    const editingTitle = ref('')

    const startEdit = (conversation: Conversation, fallbackTitle = '') => {
        editingId.value = conversation.id
        editingTitle.value = stripMarkdown(conversation.title) || fallbackTitle
    }

    const confirmEdit = async (id: string) => {
        const trimmed = editingTitle.value.trim()
        editingId.value = null
        if (trimmed && trimmed.length <= 255) {
            try {
                await actions.renameChatTitle(id, trimmed)
            } catch {
                // Revert optimistically: reload will pick up server state
            }
        }
    }

    const cancelEdit = () => {
        editingId.value = null
    }

    const onRenameKeydown = (event: KeyboardEvent, id: string) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            confirmEdit(id)
        } else if (event.key === 'Escape') {
            event.preventDefault()
            cancelEdit()
        }
    }

    const { openDialogConfirm } = useDialogConfirm()
    const requestDelete = async (id: string) => {
        try {
            const proceed = await openDialogConfirm()
            if (!proceed) {
                return
            }
            await actions.deleteChat(id)
        } catch {
            // Silently fail: next load will reconcile
        }
    }

    return {
        editingId,
        editingTitle,
        startEdit,
        confirmEdit,
        cancelEdit,
        onRenameKeydown,
        requestDelete,
    }
}
